import * as Yup from 'yup';

export const complexPasswordRegex = /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})^\S*$/;

export const passwordRequirements = 'Passwords must contain 8 characters, 1 lowercase, 1 uppercase, 1 number, and 1 symbol.';

export const PasswordSchema = Yup.object().shape({
  password: Yup.string()
    .matches(
      complexPasswordRegex,
      passwordRequirements,
    )
    .required('Please enter a password'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords do not match').required('Please confirm your password'),
});

/*
 * Shared field definitions. The email and username variants of a flow differ
 * only by which identifier they require, so they are composed from these rather
 * than copied — otherwise a change to the password rules or the phone/country
 * pairing would silently apply to only one of them.
 */
const acceptAgreementField = Yup.mixed().oneOf([true], 'Please agree to the terms');

const nameFields = {
  firstName: Yup.string().trim()
    .required('Please enter a first name'),
  lastName: Yup.string().trim()
    .required('Please enter a last name'),
};

const emailAddressField = Yup.string().email('Please enter a valid email address')
  .required('Please enter an email address');

const passwordFields = {
  password: Yup.string()
    .matches(
      complexPasswordRegex,
      passwordRequirements,
    )
    .required('Please enter a password'),
  confirmedPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'The passwords have to match!').required('Please confirm your password'),
};

/*
 * countryCode and phone each become required once the other is filled in. That
 * is a cyclic dependency, so any schema spreading these must also pass
 * PHONE_PAIR_DEPENDENCY as Yup's second argument.
 */
const interdependentPhoneFields = {
  countryCode: Yup.string()
    .when('phone', {
      is: (phone) => phone && typeof phone === 'string' && phone.trim().length > 0,
      then: (schema) => schema.required('Must select a country code if phone number is provided'),
    }),
  phone: Yup.string()
    .when('countryCode', {
      is: (countryCode) => countryCode && typeof countryCode === 'string' && countryCode.trim().length > 0,
      then: (schema) => schema.required('Must enter a phone number if country code is provided'),
    }),
};

const PHONE_PAIR_DEPENDENCY = ['phone', 'countryCode'];

/* Google and SSO flows collect no password and treat the phone number as optional. */
const optionalPhoneFields = {
  countryCode: Yup.string()
    .when('phone', {
      is: (phone) => phone && typeof phone === 'string' && phone.trim().length > 0,
      then: (schema) => schema.required('Must select a country code if phone number is provided'),
    }),
  phone: Yup.string().nullable().optional(),
};

export const RegistrationSchema = Yup.object().shape({
  acceptAgreement: acceptAgreementField,
  emailAddress: emailAddressField,
  ...nameFields,
  ...passwordFields,
  ...interdependentPhoneFields,
}, PHONE_PAIR_DEPENDENCY);

/**
 * Registration for an athlete on a usernames-enabled account. Their username is
 * assigned by their coach and rendered read-only, so it is not validated here —
 * only the email requirement is dropped relative to RegistrationSchema.
 */
export const RegistrationSchemaUsername = Yup.object().shape({
  acceptAgreement: acceptAgreementField,
  ...nameFields,
  ...passwordFields,
  ...interdependentPhoneFields,
}, PHONE_PAIR_DEPENDENCY);

export const EmailOnlySchema = Yup.object().shape({
  emailAddress: emailAddressField,
});

export const IdntyExistingUserSchema = Yup.object().shape({
  emailAddress: emailAddressField,
  password: Yup.string().required('Please enter your password'),
});

export const RegistrationSchemaGoogle = Yup.object().shape({
  acceptAgreement: acceptAgreementField,
  emailAddress: emailAddressField,
  ...nameFields,
  ...optionalPhoneFields,
});

/**
 * RegistrationSchemaGoogle without the email requirement, for SSO athletes
 * on a usernames-enabled account.
 */
export const RegistrationSchemaSsoUsername = Yup.object().shape({
  acceptAgreement: acceptAgreementField,
  ...nameFields,
  ...optionalPhoneFields,
});

export const RegistrationSchemaGooglePrimAdmin = Yup.object().shape({
  acceptAgreement: Yup.mixed().oneOf([true], 'Please agree to the terms'),
  countryCode: Yup.string()
    .when('phone', {
      is: (phone) => phone && typeof phone === 'string' && phone.trim().length > 0,
      then: (schema) => schema.required('Must select a country code if phone number is provided'),
    }),
  emailAddress: Yup.string().email('Please enter a valid email address')
    .required('Please enter an email address'),
  firstName: Yup.string().trim()
    .required('Please enter a first name'),
  lastName: Yup.string().trim()
    .required('Please enter a last name'),
  organizationName: Yup.string().trim()
    .required('Please enter an organization name'),
  phone: Yup.string()
    .when('countryCode', {
      is: (countryCode) => countryCode && typeof countryCode === 'string' && countryCode.trim().length > 0,
      then: (schema) => schema.required('Must enter a phone number if country code is provided'),
    }),
}, ['phone', 'countryCode']);

export const RegistrationSchemaIdnty = Yup.object().shape({
  acceptAgreement: Yup.mixed().oneOf([true], 'Please agree to the terms'),
  confirmedPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'The passwords have to match!').required('Please confirm your password'),
  countryCode: Yup.string()
    .when('phone', {
      is: (phone) => phone && typeof phone === 'string' && phone.trim().length > 0,
      then: (schema) => schema.required('Must select a country code if phone number is provided'),
    }),
  emailAddress: Yup.string().email('Please enter a valid email address')
    .required('Please enter an email address'),
  firstName: Yup.string().trim()
    .required('Please enter a first name'),
  lastName: Yup.string().trim()
    .required('Please enter a last name'),
  password: Yup.string()
    .matches(
      complexPasswordRegex,
      passwordRequirements,
    )
    .required('Please enter a password'),
  phone: Yup.string().nullable().optional(),
});

export const RegistrationSchemaPrimAdmin = Yup.object().shape({
  acceptAgreement: Yup.mixed().oneOf([true], 'Please agree to the terms'),
  confirmedPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'The passwords have to match!').required('Please confirm your password'),
  countryCode: Yup.string()
    .when('phone', {
      is: (phone) => phone && typeof phone === 'string' && phone.trim().length > 0,
      then: (schema) => schema.required('Must select a country code if phone number is provided'),
    }),
  emailAddress: Yup.string().email('Please enter a valid email address')
    .required('Please enter an email address'),
  firstName: Yup.string().trim()
    .required('Please enter a first name'),
  lastName: Yup.string().trim()
    .required('Please enter a last name'),
  organizationName: Yup.string().trim()
    .required('Please enter an organization name'),
  password: Yup.string()
    .matches(
      complexPasswordRegex,
      passwordRequirements,
    )
    .required('Please enter a password'),
  phone: Yup.string()
    .when('countryCode', {
      is: (countryCode) => countryCode && typeof countryCode === 'string' && countryCode.trim().length > 0,
      then: (schema) => schema.required('Must enter a phone number if country code is provided'),
    }),
}, ['phone', 'countryCode']);

export const RegistrationSchemaReseller = Yup.object().shape({
  firstName: Yup.string().trim()
    .required('Please enter a first name'),
  lastName: Yup.string().trim()
    .required('Please enter a last name'),
  emailAddress: Yup.string().email('Please enter a valid email address')
    .required('Please enter an email address'),
  password: Yup.string()
    .matches(
      complexPasswordRegex,
      passwordRequirements,
    )
    .required('Please enter a password'),
  confirmedPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'The passwords have to match!').required('Please confirm your password'),
  acceptAgreement: Yup.mixed().oneOf([true], 'Must agree to the terms'),
});

export const RegistrationSchemaSharedWorkouts = Yup.object().shape({
  acceptAgreement: Yup.mixed().oneOf([true], 'Please agree to the terms'),
  countryCode: Yup.string()
    .required('Please select a country code and enter a phone number'),
  emailAddress: Yup.string().email('Please enter a valid email address')
    .required('Please enter an email address'),
  firstName: Yup.string().trim()
    .required('Please enter a first name'),
  lastName: Yup.string().trim()
    .required('Please enter a last name'),
  organizationName: Yup.string().trim()
    .required('Please enter an organization name'),
  organizationType: Yup.string()
    .required('Please select an organization type'),
  phone: Yup.string()
    .required('Please select a country code and enter a phone number'),
  referredBy: Yup.string()
    .required('Please select how you heard about us'),
});
